import { useState, useLayoutEffect, useCallback, useMemo } from "react";
import { Check, X } from "lucide-react";

// Draws SVG bezier threads between DOM elements marked with data-conn-id, inside a position:relative container.
export const ThreadLayer = ({ containerRef, links, deps = [] }) => {
  const [paths, setPaths] = useState([]);

  const compute = useCallback(() => {
    const c = containerRef.current;
    if (!c) return;
    const cr = c.getBoundingClientRect();
    const out = [];
    for (const l of links) {
      const fe = c.querySelector(`[data-conn-id="${l.from}"]`);
      const te = c.querySelector(`[data-conn-id="${l.to}"]`);
      if (!fe || !te) continue;
      const fr = fe.getBoundingClientRect();
      const tr = te.getBoundingClientRect();
      let d, mx, my, x1, y1, x2, y2;
      if (l.loop) {
        x1 = fr.right - cr.left; y1 = fr.top + fr.height / 2 - cr.top;
        x2 = tr.right - cr.left; y2 = tr.top + tr.height / 2 - cr.top;
        const off = 56 + Math.abs(y2 - y1) * 0.18;
        d = `M ${x1},${y1} C ${x1 + off},${y1} ${x2 + off},${y2} ${x2},${y2}`;
        mx = Math.max(x1, x2) + off * 0.72; my = (y1 + y2) / 2;
      } else {
        x1 = fr.right - cr.left; y1 = fr.top + fr.height / 2 - cr.top;
        x2 = tr.left - cr.left; y2 = tr.top + tr.height / 2 - cr.top;
        const dx = Math.max((x2 - x1) / 2, 40);
        d = `M ${x1},${y1} C ${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
        mx = (x1 + x2) / 2; my = (y1 + y2) / 2;
      }
      // Only cache geometry + color + dashed; handlers are resolved from `links`
      // at render time (below) so a re-render with fresh handlers is always live.
      out.push({ key: l.key, color: l.color, dashed: l.dashed, d, mx, my, x1, y1, x2, y2 });
    }
    setPaths(out);
  }, [containerRef, links]);

  useLayoutEffect(() => {
    compute();
    const t = setTimeout(compute, 200);
    window.addEventListener("resize", compute);
    return () => { clearTimeout(t); window.removeEventListener("resize", compute); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(links.map(l => [l.key, l.from, l.to, l.dashed])), ...deps]);

  // Index the CURRENT links by key so button handlers are never stale after
  // parent-level state flips (e.g. adminOverride).
  const linkByKey = useMemo(() => {
    const m = {};
    for (const l of links) m[l.key] = l;
    return m;
  }, [links]);

  return (
    <>
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: "visible", zIndex: 5 }}>
        {paths.map((p) => (
          <g key={p.key} className="thread-enter">
            <path d={p.d} fill="none" stroke={p.color} strokeWidth="2.5"
              strokeDasharray={p.dashed ? "6 5" : "0"}
              className={p.dashed ? "thread-dashed" : "thread-solid"} strokeLinecap="round" />
            <circle cx={p.x1} cy={p.y1} r="4.5" fill={p.color} />
            <circle cx={p.x2} cy={p.y2} r="4.5" fill={p.color} />
          </g>
        ))}
      </svg>
      {paths.map((p) => {
        const l = linkByKey[p.key];
        if (!l) return null;
        return (
          <div key={"chip-" + p.key} className="absolute flex items-center gap-1"
            style={{ left: p.mx, top: p.my, transform: "translate(-50%,-50%)", zIndex: 10 }}>
            {p.dashed && l.onAccept ? (
              <div className="flex items-center gap-1 bg-white border shadow-md rounded-full pl-2 pr-1 py-0.5"
                style={{ borderColor: p.color }}>
                <span className="text-[10px] font-semibold" style={{ color: p.color }}>AI</span>
                <button data-testid={`accept-ai-link-${p.key}`} onClick={(e) => { e.stopPropagation(); l.onAccept?.(); }}
                  className="w-5 h-5 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer"
                  style={{ backgroundColor: p.color }} title="Accept AI suggestion">
                  <Check size={12} strokeWidth={3} />
                </button>
                <button data-testid={`dismiss-ai-link-${p.key}`} onClick={(e) => { e.stopPropagation(); l.onDismiss?.(); }}
                  className="w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
                  title="Dismiss suggestion">
                  <X size={12} strokeWidth={3} />
                </button>
              </div>
            ) : p.dashed && l.onRemove ? (
              /* Pending-add staged thread (emerald dashed): × unstages it */
              <button data-testid={`remove-link-${p.key}`} onClick={(e) => { e.stopPropagation(); l.onRemove?.(); }}
                className="w-5 h-5 rounded-full bg-white border shadow flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-300 transition-colors cursor-pointer"
                style={{ borderColor: p.color }} title="Cancel pending add">
                <X size={11} strokeWidth={3} />
              </button>
            ) : l.onRemove ? (
              <button data-testid={`remove-link-${p.key}`} onClick={(e) => { e.stopPropagation(); l.onRemove?.(); }}
                className="w-5 h-5 rounded-full bg-white border shadow flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-300 transition-colors cursor-pointer"
                style={{ borderColor: p.color }} title="Unlink">
                <X size={11} strokeWidth={3} />
              </button>
            ) : null}
          </div>
        );
      })}
    </>
  );
};

export const CONN_COLORS = ["#4F46E5", "#0EA5E9", "#8B5CF6", "#10B981", "#F59E0B", "#EC4899", "#14B8A6", "#6366F1"];
